import { useMemo, useState } from "react";
import { useMainContext } from "../../context";
import { useCartContext } from "./context";
import CartOrderSummary from "./components/cartOrderSummary";
import CartWayOfDelivery from "./components/cartWayOfDelivery";
import CartPaymentMethods from "./components/cartPaymentMethods";
import CartUserInfo from "./components/cartUserInfo";
import { ConfirmDestroy } from "./components/confirmDestroyModal";
import Stepper from "../../components/stepper";
import { IoCar, IoCart, IoCash } from "react-icons/io5";
import maskMoney from "../../utils/maskMoney";

const CartPage = () => {
  const { order, address } = useMainContext();
  const [step, setStep] = useState(1);
  const {
    totalOrderValue,
    onFinishOrder,
    confirmDestroy,
    setConfirmDestroy,
    removeItem,
    removeOrder,
    posItemSelected,
    textConfirm,
    funcConfirm,
    deliveryType,
    loadingCreateOrder,
  } = useCartContext();

  const steps = useMemo(
    () => [
      {
        id: 1,
        title: "Resumo",
        icon: IoCart,
        content: <CartOrderSummary />,
        buttonLabel: "Continuar para entrega",
        buttonAction: () => {
          setStep(2);
        },
      },
      {
        id: 2,
        title: "Entrega",
        icon: IoCar,
        content: <CartWayOfDelivery />,
        buttonLabel: "Continuar para pagamento",
        buttonAction: () => {
          setStep(3);
        },
      },
      {
        id: 3,
        title: "Pagamento",
        icon: IoCash,
        content: (
          <div className="space-y-4">
            <p className="text-2xl font-black uppercase text-black border-l-4 border-primary-500 pl-4 mb-6">
              {deliveryType !== "balcony"
                ? "COMO VOCÊ DESEJA PAGAR?"
                : "QUASE LÁ"}
            </p>

            {deliveryType !== "balcony" && <CartPaymentMethods />}
            <CartUserInfo />
          </div>
        ),
        buttonLabel: "Finalizar pedido",
        buttonAction: onFinishOrder,
      },
    ],
    [onFinishOrder, deliveryType],
  );

  const currentStep = useMemo(
    () => steps.find((s) => s.id === step) ?? steps[0],
    [step, steps],
  );

  const canContinue = useMemo(() => {
    if (currentStep.id === 1) {
      return order?.items?.length > 0;
    }

    if (currentStep.id === 2) {
      if (deliveryType === "balcony") {
        return !!order?.tableNumber;
      }

      if (deliveryType === "delivery") {
        return !!address?.lat && !!address?.long;
      }

      return false;
    }

    return true;
  }, [deliveryType, order, address, currentStep]);

  return (
    <>
      <div className="p-4 pb-0 md:p-8 md:pb-0 space-y-6 relative flex-grow">
        {order ? (
          <Stepper steps={steps} step={step} onChangeStep={setStep}>
            <>
              {confirmDestroy && (
                <ConfirmDestroy
                  yes={() => {
                    funcConfirm === "item"
                      ? removeItem(posItemSelected)
                      : removeOrder();
                  }}
                  no={() => {
                    setConfirmDestroy(false);
                  }}
                  open={confirmDestroy}
                  text={textConfirm}
                />
              )}
            </>
          </Stepper>
        ) : (
          <div className="flex flex-col items-center justify-center gap-4 py-20">
            <IoCart className="text-gray-400 animate-pulse" size={64} />
            <p className="text-gray-500 text-lg">
              Estamos carregando seu carrinho...
            </p>
          </div>
        )}
      </div>

      {order && (
        <div className="sticky bottom-0 left-0 right-0 flex flex-col sm:flex-row bg-white border-t border-gray-300 p-4 md:px-8 md:items-center justify-between gap-2">
          <div className="flex flex-col">
            <span className="text-xs md:text-sm font-normal leading-tight">
              TOTAL A PAGAR
            </span>
            <span className="text-2xl md:text-3xl text-primary-500 leading-tight font-bold">
              {maskMoney(totalOrderValue < 0 ? 0 : totalOrderValue)}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            {currentStep.id > 1 && (
              <button
                onClick={() => setStep((prev) => prev - 1)}
                className="uppercase px-2 md:px-8 text-center w-full sm:w-auto rounded-lg font-bold text-lg text-gray-500 h-14 disabled:opacity-50 hover:enabled:bg-gray-200 transition-colors"
              >
                Voltar
              </button>
            )}
            <button
              disabled={!canContinue || loadingCreateOrder}
              onClick={currentStep.buttonAction}
              className="bg-emerald-500 uppercase px-2 text-center w-full sm:w-auto rounded-lg font-bold text-lg text-white shadow-md h-14 disabled:bg-gray-400 hover:enabled:bg-emerald-600 transition-colors"
            >
              {currentStep.buttonLabel}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CartPage;
